/**
 * Reduced-motion preference: the in-app setting (`data-reduced-motion` on <html>, written by applyUiPreferences)
 * wins over the OS media query: 'true' = reduce, 'false' = animate even when the OS asks to reduce, absent =
 * follow the OS. Components that animate from JavaScript (CurveThumb, illustrations) check it; CSS animations
 * are stopped globally by global.css with the same precedence.
 */

import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

function read(): boolean {
  const flag = typeof document === 'undefined' ? undefined : document.documentElement.dataset.reducedMotion;
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  return typeof matchMedia === 'function' && matchMedia(QUERY).matches;
}

function subscribe(cb: () => void): () => void {
  const mql = typeof matchMedia === 'function' ? matchMedia(QUERY) : null;
  mql?.addEventListener('change', cb);
  const observer =
    typeof MutationObserver === 'function' && typeof document !== 'undefined' ? new MutationObserver(cb) : null;
  observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-reduced-motion'] });
  return () => {
    mql?.removeEventListener('change', cb);
    observer?.disconnect();
  };
}

/** True when animations should not play. */
export function prefersReducedMotion(): boolean {
  return read();
}

export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, read, () => false);
}
